import {
  AloftPrice,
  expected,
  isDefined,
  schAlertType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getAdjustedSalePriceRangeTier } from '../utils/adjustedSalePriceRangeTiers.ts';
import { getScopedAlertType } from '../utils/compTypeScoping.ts';

export const limitedAdjustedSalePriceRange: AlertDefinition = ({
  compAdjustments,
  compSequence,
  compType,
  order,
}) => {
  const compAdjustedSalePrices = compSequence
    .map((id) => compAdjustments[id]?.adjustedSalePrice.adjustment)
    .filter(isDefined);

  if (compAdjustedSalePrices.length < 2) return [];

  const maxAdjustedSalePrice = expected(AloftPrice.max(compAdjustedSalePrices));
  const minAdjustedSalePrice = expected(AloftPrice.min(compAdjustedSalePrices));
  const averageAdjustedSalePrice = AloftPrice.add(...compAdjustedSalePrices).div(
    compAdjustedSalePrices.length,
  );
  const { anchor, threshold } = getAdjustedSalePriceRangeTier(averageAdjustedSalePrice);

  if (maxAdjustedSalePrice.sub(minAdjustedSalePrice).ratio(anchor) <= threshold) {
    return [];
  }

  const thresholdPct = Math.round(threshold * 100);
  const message = `The range of adjusted sales price is greater than ${thresholdPct}%. Please confirm all comparables are reliable indicators of value as well as market-supported reasoning for the adjustments.`;

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];

  return [
    {
      type: getScopedAlertType(schAlertType.enum.limitedAdjustedSalePriceRange, compType),
      status: 'active',
      severity: 'error',
      orderType: ['saas', 'appraisify'],
      message,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips: 'Tips: Adjusted sale prices should cluster tightly (within 5-10%) to demonstrate that the comparables are genuinely similar and the analysis is sound.',
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['comment', 'action'],
        commentAlwaysRequired: false,
      },
      triggers: getAlertTriggers({
        fixRoute,
        order,
        includeUpdate: true,
      }),
    },
  ];
};
